import { isNullOrEmpty } from 'common-utils';
import { usePermission } from 'hooks/use-permission.hook';
import React from 'react';
import { Error401 } from 'ui-lib';

export enum ADMIN_PERMISSIONS {
    USERS = 'Users',
    USERS_MODIFY = 'Users Modify',
    USER_ROLE_PERMISSIONS = 'User Role Permissions',
    USER_ROLE_PERMISSIONS_MODIFY = 'User Role Permissions Modify',
    ACCOUNTS = 'Accounts',
    ACCOUNTS_MODIFY = 'Accounts Modify',
}
export enum KEY_PERMISSIONS {
    KEY_INVENTORY = 'Keys Inventory',
    KEY_DISTRIBUTION = 'Keys Distribution',
    VERIFY_KEYS = 'Verify Keys',
    KEY_INVENTORY_MODIFY = 'Keys Inventory Modify',
}
interface IPermissionContext {
    availablePermissionCodes: string[];
    runHandler: (
        permissionCode: string,
        action: () => Promise<unknown>,
        onMissingPermission?: () => unknown,
    ) => Promise<unknown>;
    hasPermission: (permissionCode: string) => boolean;
}
const PermissionContext = React.createContext<IPermissionContext>(null);

export const usePermissionContext = () => {
    const context = React.useContext(PermissionContext);
    if (!context) {
        throw new Error('usePermissionContext must be used within a PermissionProvider');
    }
    return context;
};

interface IPermissionProviderProps {
    children: JSX.Element;
    requiredPermissions?: string[];
    groups: string[];
}
const Provider = ({ children, requiredPermissions = [], groups = [] }: IPermissionProviderProps): JSX.Element => {
    const { hasPermission, availablePermissionCodes, doWithPermission } = usePermission({
        permissionGroups: groups,
    });
    const accessible = React.useMemo(
        () =>
            isNullOrEmpty(requiredPermissions)
                ? true
                : requiredPermissions.every((permission) => hasPermission(permission)),
        [requiredPermissions, hasPermission],
    );

    const runHandler = React.useCallback(
        (permissionCode: string, action: () => Promise<unknown>, onMissingPermission?: () => unknown) => {
            return doWithPermission(permissionCode, action, onMissingPermission);
        },
        [doWithPermission],
    );
    const value = React.useMemo(
        () => ({
            runHandler,
            hasPermission,
            availablePermissionCodes,
        }),
        [runHandler, hasPermission, availablePermissionCodes],
    );

    if (!accessible) {
        return <Error401 message='No permission to access' />;
    }
    return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};
export const PermissionProvider = Provider;

